"use client"

import { useState, useRef, useMemo, useEffect } from "react"
import Link from "next/link"
import Image from "next/image"
import { ChevronDown } from "lucide-react"
import { LANDING_GAMES } from "@/lib/landing-games"
import { usePathname } from "next/navigation"
import { useI18n } from "@/components/I18nProvider"

export function GameDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const games = LANDING_GAMES
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pathname = usePathname()
  const context = useI18n()
  const t = context?.t || {}

  const locale = useMemo(() => {
    const seg = pathname?.split("/")[1]
    return seg === "es" ? "es" : seg === "ja" ? "ja" : "en"
  }, [pathname])

  const hrefWithLocale = (path: string) => `/${locale}${path.startsWith('/') ? path : `/${path}`}`

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsOpen(true)
  }

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false)
    }, 150) // 150ms delay
  }

  return (
    <div 
      className="relative group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Trigger */}
      <Link
        href={hrefWithLocale("/games")}
        className="flex items-center space-x-1 text-muted-foreground hover:text-foreground transition-colors"
      >
        <span>{t.nav?.games || 'Games'}</span>
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </Link>

      {/* Invisible bridge to prevent menu from closing */}
      {isOpen && (
        <div className="absolute top-full right-0 w-full h-2 bg-transparent" />
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-[324px] origin-top-right overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a]/95 shadow-2xl shadow-black/60 backdrop-blur-xl animate-in fade-in-0 zoom-in-95 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-3">
            <span className="font-space-mono text-xs uppercase tracking-[0.2em] text-white/40">
              {t.dropdown?.allGames || 'All Games'}
            </span>
            {games.length > 0 ? (
              <span className="font-space-mono text-xs tabular-nums text-white/30">{games.length}</span>
            ) : null}
          </div>

          <div className="max-h-[min(60vh,420px)] overflow-y-auto p-1.5">
            {games.map((game) => (
              <Link
                key={game.id}
                href={hrefWithLocale(`/games/${game.slug}`)}
                className="group/game relative flex items-center gap-3 rounded-xl px-2.5 py-2 outline-none transition-colors hover:bg-white/[0.04] focus-visible:bg-white/[0.05]"
              >
                <span className="absolute inset-y-2 left-0 w-0.5 origin-top scale-y-0 rounded-full bg-primary transition-transform duration-200 group-hover/game:scale-y-100" />
                <span className="relative h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-white/[0.06] ring-1 ring-white/10">
                  <Image
                    src={game.image || '/images/games/placeholder.webp'}
                    alt={game.display_name}
                    fill
                    className="object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/placeholder.svg';
                    }}
                  />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-white">{game.display_name}</span>
                </span>
              </Link>
            ))}
          </div>

          {/* Footer */}
          <div className="border-t border-white/[0.06] p-1.5">
            <Link
              href={hrefWithLocale("/games")}
              className="flex items-center justify-between rounded-xl px-2.5 py-2.5 font-space-mono text-xs uppercase tracking-wider text-white/50 outline-none transition-colors hover:bg-white/[0.04] hover:text-white focus-visible:bg-white/[0.05]"
            >
              <span>{t.dropdown?.viewAll || 'View All Games'}</span>
              <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

export function MobileGameMenu({ onClose }: { onClose: () => void }) {
  const games = LANDING_GAMES
  const pathname = usePathname()

  const locale = useMemo(() => {
    const seg = pathname?.split("/")[1]
    return seg === "es" ? "es" : seg === "ja" ? "ja" : "en"
  }, [pathname])

  const hrefWithLocale = (path: string) => `/${locale}${path.startsWith('/') ? path : `/${path}`}`

  return (
    <div className="py-2">
      <div className="px-2 pb-2 font-space-mono text-xs uppercase tracking-[0.2em] text-white/40">
        All Games
      </div>

      <div className="space-y-0.5">
        {games.map((game) => (
            <Link
              key={game.id}
              href={hrefWithLocale(`/games/${game.slug}`)}
              className="flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-white/[0.04]"
              onClick={onClose}
            >
              <span className="relative h-8 w-8 shrink-0 overflow-hidden rounded-lg bg-white/[0.06] ring-1 ring-white/10">
                <Image
                  src={game.image || '/placeholder.svg'}
                  alt={game.display_name}
                  fill
                  className="object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/placeholder.svg';
                  }}
                />
              </span>
              <span className="truncate text-sm font-medium text-white">{game.display_name}</span>
            </Link>
          ))}

        <Link
          href={hrefWithLocale("/games")}
          className="mt-1 flex items-center justify-between rounded-xl px-2 py-2.5 font-space-mono text-xs uppercase tracking-wider text-white/50 transition-colors hover:bg-white/[0.04] hover:text-white"
          onClick={onClose}
        >
          <span>View All Games</span>
          <span aria-hidden>→</span>
        </Link>
      </div>
    </div>
  )
}
